import { useState, useRef, useEffect } from 'react';
import API from './api';

// ── Styles ────────────────────────────────────────────────────────────────────

const ANIM = `
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
}
@keyframes expertPop {
  0%   { transform: scale(0.92); opacity: 0; }
  60%  { transform: scale(1.02); }
  100% { transform: scale(1);    opacity: 1; }
}
@keyframes shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
}
`;

const s = {
  wrapper:    { minHeight: '100vh', background: 'var(--navy)', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-sans)', color: 'var(--white)' },
  header:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', height: 60, background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)', flexShrink: 0 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  logoIcon:   { width: 32, height: 32, background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 },
  logoTitle:  { fontSize: 15, fontWeight: 700 },
  logoSub:    { fontSize: 10, color: 'var(--grey)', letterSpacing: '2px', fontFamily: 'var(--font-mono)' },
  headerRight:{ display: 'flex', alignItems: 'center', gap: 10 },
  backBtn:    { background: 'transparent', border: '1px solid var(--navy-light)', color: 'var(--grey)', padding: '6px 14px', borderRadius: 6, fontSize: 11, letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)' },
  logoutBtn:  { background: 'transparent', border: '1px solid var(--navy-light)', color: 'var(--grey)', padding: '6px 14px', borderRadius: 6, fontSize: 11, letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)' },

  main:       { flex: 1, padding: '32px', maxWidth: 1100, margin: '0 auto', width: '100%' },

  inputCard:  { background: 'var(--navy-mid)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: '28px 32px', marginBottom: 28 },
  inputTitle: { fontSize: 22, fontWeight: 700, marginBottom: 6 },
  inputSub:   { fontSize: 13, color: 'var(--grey)', marginBottom: 20, lineHeight: 1.5 },
  textarea:   { width: '100%', minHeight: 110, background: 'rgba(255,255,255,0.04)', border: '1px solid var(--navy-border)', borderRadius: 10, color: 'var(--white)', fontSize: 14, padding: '14px 16px', resize: 'vertical', outline: 'none', fontFamily: 'var(--font-sans)', boxSizing: 'border-box', lineHeight: 1.6 },
  submitRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, gap: 12 },
  hint:       { fontSize: 12, color: 'var(--grey)', fontFamily: 'var(--font-mono)' },
  submitBtn:  { background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', border: 'none', color: '#fff', padding: '11px 28px', borderRadius: 8, fontSize: 13, fontWeight: 700, letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)', transition: 'opacity 0.2s' },
  submitBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },

  chips:      { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 },
  chip:       { background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)', color: '#a78bfa', padding: '5px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-mono)' },

  // File upload
  uploadZone:     { marginTop: 12, border: '1.5px dashed rgba(124,58,237,0.35)', borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'all 0.2s', background: 'rgba(124,58,237,0.03)' },
  uploadZoneDrag: { border: '1.5px dashed rgba(124,58,237,0.8)', background: 'rgba(124,58,237,0.08)' },
  uploadIcon:     { fontSize: 20, flexShrink: 0 },
  uploadLabel:    { fontSize: 12, color: 'var(--grey)', lineHeight: 1.5 },
  uploadLabelBold:{ fontWeight: 600, color: '#a78bfa' },
  fileList:       { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  fileChip:       { display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 6, padding: '4px 10px', fontSize: 11, color: '#c4b5fd', fontFamily: 'var(--font-mono)', maxWidth: 260 },
  fileName:       { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 },
  fileRemoveBtn:  { background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0, flexShrink: 0 },

  // Progress bar
  progressCard: { background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 14, padding: '20px 28px', marginBottom: 28, animation: 'fadeUp 0.3s ease' },
  progressHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  progressTitle:  { fontSize: 12, fontWeight: 700, color: '#a78bfa', fontFamily: 'var(--font-mono)', letterSpacing: '1px' },
  progressPct:    { fontSize: 12, fontWeight: 700, color: '#a78bfa', fontFamily: 'var(--font-mono)' },
  progressExpert: { fontSize: 13, color: 'var(--grey)', marginBottom: 10, animation: 'pulse 1.8s ease-in-out infinite' },
  progressTrack:  { height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 4, overflow: 'hidden' },
  progressFill:   { height: '100%', background: 'linear-gradient(90deg,#7c3aed,#818cf8)', borderRadius: 4, transition: 'width 0.4s ease' },

  // Expert cards grid
  expertsGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 18, marginBottom: 28 },
  expertCard:   { background: 'var(--navy-mid)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '20px 22px', animation: 'expertPop 0.45s ease forwards' },
  expertHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  expertIcon:   { fontSize: 24 },
  expertMeta:   { flex: 1 },
  expertName:   { fontSize: 14, fontWeight: 700 },
  expertDomain: { fontSize: 11, color: 'var(--grey)', fontFamily: 'var(--font-mono)', marginTop: 2 },
  expertDivider:{ height: 1, background: 'var(--navy-border)', marginBottom: 12 },
  expertBody:   { fontSize: 13, color: '#cbd5e1', lineHeight: 1.7 },
  expertBullet: { display: 'flex', gap: 8, marginBottom: 6 },
  bulletDot:    { color: '#a78bfa', flexShrink: 0, marginTop: 2 },
  expertHighlight: { marginTop: 10, padding: '7px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-mono)' },

  // Synthesis
  synthesisCard:  { background: 'var(--navy-mid)', border: '1px solid rgba(124,58,237,0.4)', borderRadius: 14, padding: '28px 32px', marginBottom: 28, animation: 'expertPop 0.5s ease forwards' },
  synthesisTitle: { fontSize: 18, fontWeight: 700, marginBottom: 6 },
  synthesisSub:   { fontSize: 12, color: 'var(--grey)', fontFamily: 'var(--font-mono)', marginBottom: 20, letterSpacing: '1px' },
  synthesisBody:  { fontSize: 14, color: '#cbd5e1', lineHeight: 1.8 },
  synthSection:   { marginBottom: 18 },
  synthHeading:   { fontSize: 13, fontWeight: 700, color: '#a78bfa', fontFamily: 'var(--font-mono)', letterSpacing: '1px', marginBottom: 8 },
  synthBullet:    { display: 'flex', gap: 10, marginBottom: 7, alignItems: 'flex-start' },
  synthDot:       { color: '#7c3aed', flexShrink: 0, fontWeight: 700, marginTop: 1 },
  verdictBox:     { marginTop: 20, padding: '18px 22px', background: 'linear-gradient(135deg,rgba(124,58,237,0.15),rgba(79,70,229,0.08))', border: '1px solid rgba(124,58,237,0.5)', borderRadius: 10 },
  verdictLabel:   { fontSize: 11, fontWeight: 700, color: '#a78bfa', fontFamily: 'var(--font-mono)', letterSpacing: '2px', marginBottom: 8 },
  verdictText:    { fontSize: 14, lineHeight: 1.7, color: '#e2e8f0' },

  // Error
  errorBox: { background: 'rgba(232,0,29,0.08)', border: '1px solid rgba(232,0,29,0.3)', borderRadius: 10, padding: '14px 18px', color: '#ff6b7a', fontSize: 13, marginBottom: 24 },
};

// ── Example topics ────────────────────────────────────────────────────────────

const EXAMPLES = [
  "Should we move to paperless crew documentation across the fleet?",
  "What is the impact of introducing drone inspections for hull surveys?",
  "Can AI replace traditional onboard safety drills?",
  "Our Chief Officer wants to reduce lifeboat drill frequency to quarterly.",
  "We are considering outsourcing the DPA function to a third-party manager.",
];

// ── Markdown-lite renderer ────────────────────────────────────────────────────

function renderAnalysis(text, accentColor) {
  if (!text) return null;
  const lines = text.split('\n').filter(l => l !== undefined);
  const elements = [];
  let key = 0;

  for (const line of lines) {
    const t = line.trim();
    if (!t) { elements.push(<div key={key++} style={{ height: 8 }} />); continue; }

    // Bold heading line: **Key Safety Concern**: ...
    const headingMatch = t.match(/^\*\*(.+?)\*\*:?\s*(.*)$/);
    if (headingMatch) {
      elements.push(
        <div key={key++} style={{ marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: accentColor, fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' }}>
            {headingMatch[1]}
          </span>
          {headingMatch[2] && <span style={{ fontSize: 13, color: '#e2e8f0', marginLeft: 6 }}>{headingMatch[2]}</span>}
        </div>
      );
      continue;
    }

    // Bullet point
    if (t.startsWith('- ') || t.startsWith('• ')) {
      elements.push(
        <div key={key++} style={s.expertBullet}>
          <span style={{ ...s.bulletDot, color: accentColor }}>▸</span>
          <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>{t.slice(2)}</span>
        </div>
      );
      continue;
    }

    // Numbered list
    const numMatch = t.match(/^(\d+)\.\s+(.+)$/);
    if (numMatch) {
      elements.push(
        <div key={key++} style={s.expertBullet}>
          <span style={{ ...s.bulletDot, color: accentColor, minWidth: 18 }}>{numMatch[1]}.</span>
          <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>{numMatch[2]}</span>
        </div>
      );
      continue;
    }

    elements.push(<p key={key++} style={{ margin: '0 0 6px', fontSize: 13, color: '#cbd5e1', lineHeight: 1.65 }}>{t}</p>);
  }
  return elements;
}

function renderSynthesis(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let key = 0;
  let inVerdict = false;
  let verdictLines = [];

  const flushVerdict = () => {
    if (verdictLines.length) {
      elements.push(
        <div key={key++} style={s.verdictBox}>
          <div style={s.verdictLabel}>PANEL VERDICT</div>
          <div style={s.verdictText}>{verdictLines.join(' ')}</div>
        </div>
      );
      verdictLines = [];
    }
    inVerdict = false;
  };

  for (const line of lines) {
    const t = line.trim();
    if (!t) { if (!inVerdict) elements.push(<div key={key++} style={{ height: 8 }} />); continue; }

    if (t.match(/\*\*Panel Verdict\*\*/i) || t.match(/Panel Verdict/i)) {
      inVerdict = true;
      continue;
    }
    if (inVerdict) { verdictLines.push(t); continue; }

    const headingMatch = t.match(/^\*\*(.+?)\*\*\s*$/);
    if (headingMatch) {
      flushVerdict();
      elements.push(<div key={key++} style={s.synthHeading}>{headingMatch[1].toUpperCase()}</div>);
      continue;
    }

    if (t.startsWith('- ') || t.startsWith('• ')) {
      elements.push(
        <div key={key++} style={s.synthBullet}>
          <span style={s.synthDot}>▸</span>
          <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.65 }}>{t.slice(2)}</span>
        </div>
      );
      continue;
    }
    const numMatch = t.match(/^(\d+)\.\s+(.+)$/);
    if (numMatch) {
      elements.push(
        <div key={key++} style={s.synthBullet}>
          <span style={{ ...s.synthDot, minWidth: 20 }}>{numMatch[1]}.</span>
          <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.65 }}>{numMatch[2]}</span>
        </div>
      );
      continue;
    }
    elements.push(<p key={key++} style={{ margin: '0 0 6px', fontSize: 13, color: '#cbd5e1', lineHeight: 1.7 }}>{t}</p>);
  }
  flushVerdict();
  return elements;
}

// ── Main component ────────────────────────────────────────────────────────────

const ACCEPTED = '.pdf,.docx,.doc,.xlsx,.xlsm,.xls,.pptx,.txt,.md';

export default function BrainStorm({ setPage, onLogout }) {
  const [topic, setTopic]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [progress, setProgress]       = useState(null);
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState('');
  const [attachedFiles, setAttached]  = useState([]);   // File[]
  const [dragOver, setDragOver]       = useState(false);
  const pollRef                       = useRef(null);
  const bottomRef                     = useRef(null);
  const fileInputRef                  = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList);
    setAttached(prev => {
      const existing = new Set(prev.map(f => f.name));
      return [...prev, ...incoming.filter(f => !existing.has(f.name))];
    });
  };

  const removeFile = (name) => setAttached(prev => prev.filter(f => f.name !== name));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const startBrainstorm = async () => {
    if (!topic.trim() || loading) return;
    setLoading(true);
    setResult(null);
    setError('');
    setProgress({ step: 0, total_steps: 6, pct: 0, current_expert: 'Assembling the panel…', current_expert_icon: '⚙️' });

    pollRef.current = setInterval(async () => {
      try {
        const p = await API.get('/brainstorm/progress');
        setProgress(p.data);
      } catch { /* ignore */ }
    }, 600);

    try {
      const form = new FormData();
      form.append('topic', topic.trim());
      attachedFiles.forEach(f => form.append('files', f));

      const res = await API.post('/brainstorm/analyze', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      clearInterval(pollRef.current);
      if (res.data.error && !res.data.expert_results?.length) {
        setError(res.data.error);
      } else {
        setResult(res.data);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (e) {
      clearInterval(pollRef.current);
      setError(e.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') startBrainstorm();
  };

  return (
    <div style={s.wrapper}>
      <style>{ANIM}</style>

      {/* Header */}
      <header style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.logoIcon}>🧠</div>
          <div>
            <div style={s.logoTitle}>BrainStorm™</div>
            <div style={s.logoSub}>MULTI-AGENT PANEL ANALYSIS</div>
          </div>
        </div>
        <div style={s.headerRight}>
          <button style={s.backBtn} onClick={() => setPage('dashboard')}>← BACK</button>
          <button style={s.logoutBtn} onClick={onLogout}>LOGOUT</button>
        </div>
      </header>

      <div style={s.main}>

        {/* Input card */}
        <div style={s.inputCard}>
          <div style={s.inputTitle}>What's on the table?</div>
          <div style={s.inputSub}>
            Submit any topic, hypothesis, or operational question. Five maritime expert agents will each
            analyse it from their domain — then a synthesis panel will converge on recommendations.
          </div>

          {/* Example chips */}
          <div style={s.chips}>
            {EXAMPLES.map((ex, i) => (
              <span key={i} style={s.chip} onClick={() => setTopic(ex)}>{ex}</span>
            ))}
          </div>

          <textarea
            style={s.textarea}
            placeholder="Describe the topic, scenario, or hypothesis you want the expert panel to analyse…"
            value={topic}
            onChange={e => setTopic(e.target.value)}
            onKeyDown={handleKey}
            disabled={loading}
          />

          {/* File upload zone */}
          <div
            style={{ ...s.uploadZone, ...(dragOver ? s.uploadZoneDrag : {}) }}
            onClick={() => !loading && fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <span style={s.uploadIcon}>📎</span>
            <div style={s.uploadLabel}>
              <span style={s.uploadLabelBold}>Attach supporting documents</span>
              {' '}— drag & drop or click to browse
              <br />
              <span style={{ fontSize: 11 }}>PDF, DOCX, DOC, XLSX, PPTX, TXT · The panel will read them as context</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED}
              style={{ display: 'none' }}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
              disabled={loading}
            />
          </div>

          {/* Attached files */}
          {attachedFiles.length > 0 && (
            <div style={s.fileList}>
              {attachedFiles.map(f => (
                <div key={f.name} style={s.fileChip}>
                  <span>📄</span>
                  <span style={s.fileName} title={f.name}>{f.name}</span>
                  <button
                    style={s.fileRemoveBtn}
                    onClick={e => { e.stopPropagation(); removeFile(f.name); }}
                    disabled={loading}
                    title="Remove"
                  >×</button>
                </div>
              ))}
            </div>
          )}

          <div style={s.submitRow}>
            <span style={s.hint}>
              Ctrl+Enter to submit · ~30 seconds for full analysis
              {attachedFiles.length > 0 && ` · ${attachedFiles.length} document${attachedFiles.length > 1 ? 's' : ''} attached`}
            </span>
            <button
              style={{ ...s.submitBtn, ...(loading || !topic.trim() ? s.submitBtnDisabled : {}) }}
              onClick={startBrainstorm}
              disabled={loading || !topic.trim()}
            >
              {loading ? 'ANALYSING…' : '🧠 START BRAINSTORM'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && <div style={s.errorBox}>⚠ {error}</div>}

        {/* Progress bar */}
        {progress && (
          <div style={s.progressCard}>
            <div style={s.progressHeader}>
              <span style={s.progressTitle}>
                {progress.current_expert_icon} &nbsp;
                STEP {progress.step} OF {progress.total_steps} — PANEL IN SESSION
              </span>
              <span style={s.progressPct}>{progress.pct}%</span>
            </div>
            <div style={s.progressExpert}>{progress.current_expert}</div>
            <div style={s.progressTrack}>
              <div style={{ ...s.progressFill, width: `${progress.pct}%` }} />
            </div>
          </div>
        )}

        {/* Expert results */}
        {result && (
          <>
            <div style={{ fontSize: 12, color: 'var(--grey)', fontFamily: 'var(--font-mono)', letterSpacing: '1px', marginBottom: 14 }}>
              EXPERT PANEL — RE: "{result.topic}"
            </div>
            <div style={s.expertsGrid}>
              {result.expert_results.map((exp, i) => (
                <div
                  key={exp.id}
                  style={{ ...s.expertCard, borderColor: `${exp.color}33`, animationDelay: `${i * 0.08}s` }}
                >
                  <div style={s.expertHeader}>
                    <span style={s.expertIcon}>{exp.icon}</span>
                    <div style={s.expertMeta}>
                      <div style={s.expertName}>{exp.name}</div>
                      <div style={s.expertDomain}>{exp.domain}</div>
                    </div>
                  </div>
                  <div style={{ ...s.expertDivider, borderColor: `${exp.color}33` }} />
                  <div style={s.expertBody}>
                    {renderAnalysis(exp.analysis, exp.color)}
                  </div>
                </div>
              ))}
            </div>

            {/* Synthesis */}
            {result.synthesis && (
              <div style={s.synthesisCard}>
                <div style={s.synthesisTitle}>🧩 Panel Synthesis</div>
                <div style={s.synthesisSub}>CONVERGENCE ACROSS ALL EXPERT VIEWS</div>
                <div style={s.synthesisBody}>
                  {renderSynthesis(result.synthesis)}
                </div>
              </div>
            )}
          </>
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  );
}
